import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_ENDPOINTS, getCurrentBrand } from '../config/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  Fade,
  Skeleton,
  Pagination,
  LinearProgress,
  Paper,
  Tooltip,
  Avatar,
  Stack,
  Container,
  CardMedia,
  CardActions,
  Zoom,
  Slide,
  TextField,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Image as ImageIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  AutoAwesome as AutoAwesomeIcon,
  Language as LanguageIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material';
import { heicTo } from 'heic-to';

const StoreImages = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedImage, setSelectedImage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateFiles, setDuplicateFiles] = useState([]);

  // Bulk selection states
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Create Image states
  const [createImageDialogOpen, setCreateImageDialogOpen] = useState(false);
  const [createImageFile, setCreateImageFile] = useState(null);
  const [imagePrompt, setImagePrompt] = useState('');
  const [generatingImage, setGeneratingImage] = useState(false);

  // Fetch images from API
  const fetchImages = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const offset = (page - 1) * itemsPerPage;
      const params = new URLSearchParams({
        limit: itemsPerPage.toString(),
        offset: offset.toString(),
        brand: getCurrentBrand()
      });

      const response = await fetch(`${API_ENDPOINTS.STORE_IMAGES}?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch images');
      }

      const data = await response.json();
      setImages(data.images || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error('Error fetching images:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage]);

  // Initial load
  useEffect(() => {
    fetchImages(currentPage);
  }, [currentPage, fetchImages]);

  // Handle page change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  // Handle image click
  const handleImageClick = (image) => {
    setSelectedImage(image);
    setDialogOpen(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedImage(null);
  };

  // Handle menu open
  const handleMenuOpen = (event, image) => {
    setAnchorEl(event.currentTarget);
    setSelectedImage(image);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedImage(null);
  };

  // Bulk selection handlers
  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAllOnPage = () => {
    const newSelected = new Set(selectedIds);
    images.forEach(image => {
      newSelected.add(image.id);
    });
    setSelectedIds(newSelected);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    if (!window.confirm(t('storeImages.confirmBulkDelete') || 'Are you sure you want to delete the selected items?')) {
      return;
    }

    setBulkLoading(true);
    try {
      const deletePromises = Array.from(selectedIds).map(id =>
        fetch(`${API_ENDPOINTS.STORE_IMAGES}/${id}?brand=${getCurrentBrand()}`, {
          method: 'DELETE',
        })
      );

      await Promise.all(deletePromises);

      clearSelection();
      await fetchImages(currentPage);

      const event = new CustomEvent('show-alert', {
        detail: {
          message: t('storeImages.bulkDeleteSuccess') || 'Selected items deleted successfully',
          severity: 'success'
        }
      });
      window.dispatchEvent(event);

    } catch (err) {
      console.error('Error deleting items:', err);
      setError(err.message);
    } finally {
      setBulkLoading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    const errors = [];

    files.forEach((file, index) => {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        errors.push(`${file.name}: Not an image file`);
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name}: File size must be less than 10MB`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join(', '));
    } else {
      setError(null);
    }

    setSelectedFiles(validFiles);
  };

  // Handle perform upload (logic extracted from handleUpload)
  const performUpload = async (filesToUpload = selectedFiles) => {
    if (filesToUpload.length === 0) {
      setError(t('storeImages.selectFilesUpload'));
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);
      setUploadingFiles([]);

      const totalFiles = filesToUpload.length;
      let completedFiles = 0;
      const results = [];

      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];

        // Update uploading files list
        setUploadingFiles(prev => [...prev, { name: file.name, status: 'uploading' }]);

        try {
          let uploadFile = file;

          // Check if HEIC and convert
          if (file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
            try {
              console.log(`Converting ${file.name} to JPEG...`);
              const blob = await heicTo({ blob: file, type: 'image/jpeg' });
              const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
              uploadFile = new File([blob], newName, { type: 'image/jpeg' });
              console.log(`Converted to ${uploadFile.name}`);
            } catch (convErr) {
              console.error(`Conversion failed for ${file.name}:`, convErr);
              throw new Error(`HEIC conversion failed: ${convErr.message}`);
            }
          }

          // Create FormData
          const formData = new FormData();
          formData.append('image', uploadFile);

          // Upload to Cloudflare R2
          const uploadResponse = await fetch(API_ENDPOINTS.UPLOAD_TO_R2, {
            method: 'POST',
            body: formData,
          });

          if (!uploadResponse.ok) {
            throw new Error(`Failed to upload ${file.name} to R2`);
          }

          const uploadResult = await uploadResponse.json();

          // Insert into lokss2 table
          const insertResponse = await fetch(`${API_ENDPOINTS.STORE_IMAGES}?brand=${getCurrentBrand()}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              image_url: uploadResult.url,
              is_active: true
            }),
          });

          if (!insertResponse.ok) {
            throw new Error(`Failed to save ${file.name} to database`);
          }

          // Update status to completed
          setUploadingFiles(prev =>
            prev.map(f => f.name === file.name ? { ...f, status: 'completed' } : f)
          );

          results.push({ file: file.name, success: true, url: uploadResult.url });
          completedFiles++;
          setUploadProgress((completedFiles / totalFiles) * 100);

        } catch (fileError) {
          console.error(`Error uploading ${file.name}:`, fileError);
          setUploadingFiles(prev =>
            prev.map(f => f.name === file.name ? { ...f, status: 'error', error: fileError.message } : f)
          );
          results.push({ file: file.name, success: false, error: fileError.message });
          completedFiles++;
          setUploadProgress((completedFiles / totalFiles) * 100);
        }
      }

      // Show results
      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;

      if (failed > 0) {
        const failedFiles = results.filter(r => !r.success).map(r => `${r.file}: ${r.error}`).join(', ');
        setError(`${successful} ${t('storeImages.filesUploaded')}, ${failed} ${t('storeImages.failedFiles')}: ${failedFiles}`);
      } else {
        setError(null);
      }

      // Close dialog and refresh images after a short delay
      setTimeout(() => {
        setUploadDialogOpen(false);
        setSelectedFiles([]);
        setUploadProgress(0);
        setUploadingFiles([]);
        fetchImages(currentPage);
      }, 2000);

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  // Handle upload check
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError(t('storeImages.selectFilesUpload'));
      return;
    }

    try {
      setUploading(true);
      const params = new URLSearchParams({
        limit: '10000',
        brand: getCurrentBrand()
      });
      const response = await fetch(`${API_ENDPOINTS.STORE_IMAGES}?${params}`);

      if (response.ok) {
        const data = await response.json();
        const existingImages = data.images || [];

        const duplicates = selectedFiles.filter(file => {
          return existingImages.some(img =>
            img.image_url && img.image_url.toLowerCase().includes(file.name.toLowerCase())
          );
        }).map(f => f.name);

        if (duplicates.length > 0) {
          setDuplicateFiles(duplicates);
          setDuplicateDialogOpen(true);
          setUploading(false);
          return;
        }
      }
    } catch (err) {
      console.error('Error checking duplicates:', err);
    }

    await performUpload();
  };

  const handleConfirmUpload = async () => {
    setDuplicateDialogOpen(false);
    await performUpload();
  };

  const handleSkipDuplicates = async () => {
    const filesToUpload = selectedFiles.filter(file => !duplicateFiles.includes(file.name));
    setDuplicateDialogOpen(false);

    if (filesToUpload.length === 0) {
      setUploadDialogOpen(false);
      setSelectedFiles([]);
      return;
    }

    await performUpload(filesToUpload);
  };

  // Close upload dialog
  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFiles([]);
    setUploadProgress(0);
    setUploadingFiles([]);
    setError(null);
  };

  // Handle create image file selection
  const handleCreateImageFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCreateImageFile(file);
    }
  };

  // Handle create image generation
  const handleCreateImage = async () => {
    if (!createImageFile || !imagePrompt.trim()) {
      setError(t('storeImages.selectFilesUpload'));
      return;
    }

    try {
      setGeneratingImage(true);
      setError(null);

      // First upload the original image to R2
      const formData = new FormData();
      formData.append('image', createImageFile);

      const uploadResponse = await fetch(API_ENDPOINTS.UPLOAD_TO_R2, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image to R2');
      }

      const uploadResult = await uploadResponse.json();

      // Generate AI image with text overlay
      const generateResponse = await fetch(API_ENDPOINTS.GENERATE_AI_IMAGE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageUrl: uploadResult.url,
          prompt: imagePrompt
        }),
      });

      if (!generateResponse.ok) {
        throw new Error('Failed to generate AI image');
      }

      const generatedResult = await generateResponse.json();

      if (!generatedResult.success) {
        throw new Error('AI image generation failed');
      }

      // Save the generated image to database
      const saveResponse = await fetch(`${API_ENDPOINTS.STORE_IMAGES}?brand=${getCurrentBrand()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_url: generatedResult.imageUrl,
          is_active: true
        }),
      });

      if (!saveResponse.ok) {
        throw new Error('Failed to save generated image');
      }

      // Close dialog and refresh images
      setCreateImageDialogOpen(false);
      setCreateImageFile(null);
      setImagePrompt('');
      await fetchImages(currentPage);

    } catch (err) {
      console.error('Error creating image:', err);
      setError(err.message);
    } finally {
      setGeneratingImage(false);
    }
  };

  // Close create image dialog
  const handleCloseCreateImageDialog = () => {
    setCreateImageDialogOpen(false);
    setCreateImageFile(null);
    setImagePrompt('');
    setError(null);
  };

  // Handle image deletion
  const handleDeleteImage = async (imageId) => {
    if (!window.confirm(t('storeImages.deleteConfirm'))) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const brand = getCurrentBrand();
      const response = await fetch(`${API_ENDPOINTS.STORE_IMAGES}/${imageId}?brand=${brand}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(t('storeImages.failedToDeleteImage'));
      }

      // Refresh the images list
      await fetchImages(currentPage);

    } catch (err) {
      console.error('Error deleting image:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Check if image is HEIC format
  const isHeicImage = (imageUrl) => {
    if (!imageUrl) return false;
    const urlLower = imageUrl.toLowerCase();
    return urlLower.includes('.heic') || urlLower.includes('.heif');
  };

  const handlePageSizeChange = (e) => {
    setItemsPerPage(e.target.value);
    setCurrentPage(1);
  };

  return (
    <>
      <style>
        {`
          .card-overlay {
            opacity: 0;
            transition: opacity 0.3s ease;
          }
          .MuiCard-root:hover .card-overlay {
            opacity: 1;
          }
          @keyframes loading {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            25% { transform: translateY(-10px) rotate(1deg); }
            50% { transform: translateY(-20px) rotate(0deg); }
            75% { transform: translateY(-10px) rotate(-1deg); }
          }
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          .shimmer {
            position: relative;
            overflow: hidden;
          }
          .shimmer::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
            animation: shimmer 2s infinite;
          }
          .pulse-animation {
            animation: pulse 2s ease-in-out infinite;
          }
        `}
      </style>
      <Container
        maxWidth={false}
        sx={{
          py: { xs: 2, md: 4 },
          px: { xs: 2, md: 3 },
          width: '100%'
        }}
      >
        {/* Main Content Card Container */}
        <Paper
          elevation={8}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
            border: '1px solid rgba(255,255,255,0.2)'
          }}
        >
          {/* Enhanced Professional Header Section */}
          <Paper
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
              color: 'white',
              borderRadius: 0,
              p: { xs: 3, md: 5 },
              position: 'relative',
              overflow: 'hidden',
              boxShadow: 'none'
            }}
          >
            {/* Animated Background Pattern */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.08,
                backgroundImage: `
              radial-gradient(circle at 20% 20%, white 1px, transparent 1px),
              radial-gradient(circle at 80% 80%, white 1px, transparent 1px),
              radial-gradient(circle at 40% 40%, white 1px, transparent 1px)
            `,
                backgroundSize: '60px 60px, 80px 80px, 40px 40px',
                animation: 'float 20s ease-in-out infinite'
              }}
            />

            {/* Floating Orbs */}
            <Box
              sx={{
                position: 'absolute',
                top: '10%',
                right: '15%',
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                animation: 'float 15s ease-in-out infinite reverse'
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: '20%',
                left: '10%',
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                backdropFilter: 'blur(8px)',
                animation: 'float 12s ease-in-out infinite'
              }}
            />

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ flexGrow: 1, mb: { xs: 3, md: 0 } }}>
                <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 2 }}>
                  <Avatar sx={{
                    bgcolor: 'rgba(255,255,255,0.25)',
                    width: { xs: 56, md: 72 },
                    height: { xs: 56, md: 72 },
                    border: '3px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05) rotate(5deg)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.2)'
                    }
                  }}>
                    <ImageIcon sx={{ fontSize: { xs: 28, md: 36 }, color: 'white' }} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 900,
                        mb: 0.5,
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                        textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                        background: 'linear-gradient(45deg, #ffffff 0%, #f0f8ff 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.02em'
                      }}
                    >
                      {t('storeImages.title')}
                    </Typography>
                    <Typography variant="h5" sx={{
                      opacity: 0.95,
                      fontWeight: 500,
                      fontSize: { xs: '0.9rem', sm: '1rem', md: '1.3rem' },
                      textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                      letterSpacing: '0.01em'
                    }}>
                      {t('storeImages.subtitle')}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ alignItems: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => setUploadDialogOpen(true)}
                  className="pulse-animation"
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.25)',
                    color: 'white',
                    backdropFilter: 'blur(15px)',
                    border: '2px solid rgba(255,255,255,0.4)',
                    px: { xs: 3, md: 5 },
                    py: { xs: 1.5, md: 2.5 },
                    borderRadius: 4,
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: { xs: '0.9rem', md: '1.1rem' },
                    minHeight: { xs: 48, md: 64 },
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.35)',
                      transform: 'translateY(-4px) scale(1.02)',
                      boxShadow: '0 16px 40px rgba(0,0,0,0.25)',
                      borderColor: 'rgba(255,255,255,0.6)',
                      animation: 'none'
                    },
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  <Box sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('storeImages.uploadImages')}</Box>
                  <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>{t('common.upload') || 'Upload'}</Box>
                </Button>
              </Stack>
            </Stack>

          </Paper>

          {/* Platform Selection Switcher */}
          <Box sx={{
            width: '100%',
            pt: 0,
            pb: 0
          }}>
            <Paper
              elevation={0}
              sx={{
                mb: 0,
                borderRadius: 0,
                overflow: 'hidden',
                background: '#ffffff',
                border: 'none',
                boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)',
                position: 'relative'
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Stack direction="row" spacing={0} sx={{ width: '100%' }}>
                  {[
                    {
                      id: 'generator',
                      labelKey: 'aiGenerator.xhsContentGeneratorTab',
                      icon: LanguageIcon,
                      color: '#ff6b6b',
                      gradient: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                      glowColor: 'rgba(255, 107, 107, 0.3)',
                      accentColor: '#ff8a80'
                    },
                    {
                      id: 'images',
                      labelKey: 'aiGenerator.xhsImagesTab',
                      icon: ImageIcon,
                      color: '#ff6b6b',
                      gradient: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                      glowColor: 'rgba(255, 107, 107, 0.3)',
                      accentColor: '#ff8a80'
                    }
                  ].map((platform) => {
                    const IconComponent = platform.icon;
                    const currentHash = location.hash || '#images';
                    const isSelected = currentHash === `#${platform.id}` || (currentHash === '' && platform.id === 'images');
                    return (
                      <Button
                        key={platform.id}
                        onClick={() => navigate(`${location.pathname}#${platform.id}`)}
                        sx={{
                          flex: 1,
                          py: { xs: 2.5, md: 3.25 },
                          px: { xs: 2.5, md: 4 },
                          borderRadius: 0,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: { xs: '0.95rem', md: '1.05rem' },
                          color: isSelected ? 'white' : '#2c3e50',
                          background: isSelected ? platform.gradient : '#ffffff',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: isSelected ? `0 4px 16px ${platform.glowColor}, 0 2px 8px rgba(255, 107, 107, 0.2)` : 'none',
                          border: 'none',
                          minHeight: { xs: 56, md: 68 },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: isSelected ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                            opacity: 0,
                            transition: 'opacity 0.3s ease',
                            zIndex: 1
                          },
                          '&:hover': {
                            background: isSelected ? platform.gradient : 'rgba(255, 107, 107, 0.06)',
                            boxShadow: isSelected ? `0 6px 20px ${platform.glowColor}, 0 3px 10px rgba(255, 107, 107, 0.25)` : '0 2px 8px rgba(0, 0, 0, 0.04)',
                            transform: isSelected ? 'translateY(-1px)' : 'none',
                            '&::before': {
                              opacity: isSelected ? 1 : 0
                            },
                            '& .platform-icon': {
                              transform: 'scale(1.08)',
                              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                            }
                          },
                          '&:active': {
                            transform: 'scale(0.98) translateY(0)',
                            transition: 'all 0.1s ease'
                          }
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="center"
                          spacing={1.5}
                          sx={{ position: 'relative', zIndex: 2, width: '100%' }}
                        >
                          <IconComponent
                            className="platform-icon"
                            sx={{
                              fontSize: { xs: 20, md: 23 },
                              color: 'inherit',
                              transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                              filter: isSelected ? 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))' : 'none'
                            }}
                          />
                          <Typography
                            className="platform-text"
                            variant="body1"
                            sx={{
                              fontWeight: isSelected ? 700 : 600,
                              fontSize: 'inherit',
                              letterSpacing: '0.015em',
                              transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                              textShadow: isSelected ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'
                            }}
                          >
                            {t(platform.labelKey)}
                          </Typography>
                        </Stack>
                      </Button>
                    );
                  })}
                </Stack>
              </Box>
            </Paper>
          </Box>

          {/* Bulk Action Bar */}
          <Slide direction="down" in={selectedIds.size > 0} mountOnEnter unmountOnExit>
            <Paper
              elevation={4}
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                m: 2,
                p: 2,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {selectedIds.size} {t('common.selected') || 'selected'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearSelection}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.5)',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  {t('common.cancel') || 'Cancel'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={selectAllOnPage}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.5)',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
                  }}
                >
                  {t('common.selectAll') || 'Select All'}
                </Button>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={bulkLoading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                  onClick={handleBulkDelete}
                  disabled={bulkLoading}
                  sx={{
                    bgcolor: 'rgba(231, 76, 60, 0.9)',
                    '&:hover': { bgcolor: '#c0392b' },
                    boxShadow: 'none'
                  }}
                >
                  {t('common.delete') || 'Delete'}
                </Button>
              </Stack>
            </Paper>
          </Slide>

          {/* Content Section */}
          <Box sx={{
            p: { xs: 3, md: 4 },
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '1px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(52, 152, 219, 0.3) 50%, transparent 100%)'
            }
          }}>

            {/* Error Alert */}
            {error && (
              <Slide direction="down" in={!!error} mountOnEnter unmountOnExit>
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      fontSize: '1.25rem'
                    }
                  }}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => setError(null)}
                    >
                      <ErrorIcon fontSize="inherit" />
                    </IconButton>
                  }
                >
                  {error}
                </Alert>
              </Slide>
            )}


            {/* Enhanced Professional Loading State */}
            {loading && (
              <Fade in timeout={300}>
                <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                  {[...Array(8)].map((_, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={index}>
                      <Card
                        sx={{
                          borderRadius: 4,
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                          border: '2px solid transparent',
                          background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #2c3e50 0%, #3498db 100%) border-box',
                          animation: 'float 6s ease-in-out infinite',
                          animationDelay: `${index * 0.2}s`,
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 12px 30px rgba(52, 152, 219, 0.15)'
                          }
                        }}
                      >
                        <Box className="shimmer">
                          <Skeleton
                            variant="rectangular"
                            height={240}
                            sx={{
                              background: 'linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%)',
                              backgroundSize: '200% 100%',
                              animation: 'loading 1.8s infinite',
                              borderBottom: '1px solid #e9ecef'
                            }}
                          />
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                          <Stack spacing={2}>
                            <Skeleton variant="text" height={24} width="60%" sx={{ mb: 1 }} />
                            <Skeleton variant="text" height={16} width="80%" sx={{ mb: 2 }} />
                            <Stack direction="row" spacing={1}>
                              <Skeleton variant="rectangular" height={24} width={60} sx={{ borderRadius: 2 }} />
                              <Skeleton variant="rectangular" height={24} width={70} sx={{ borderRadius: 2 }} />
                            </Stack>
                          </Stack>
                        </CardContent>
                        <CardActions sx={{ p: 3, pt: 0, justifyContent: 'center' }}>
                          <Skeleton variant="rectangular" height={36} width={120} sx={{ borderRadius: 2 }} />
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Fade>
            )}

            {/* Enhanced Images Grid */}
            {!loading && (
              <Fade in timeout={500}>
                <Box sx={{
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '60%',
                    height: '2px',
                    background: 'linear-gradient(90deg, transparent 0%, rgba(52, 152, 219, 0.2) 50%, transparent 100%)',
                    borderRadius: '2px'
                  }
                }}>
                  {images.length === 0 ? (
                    <Paper
                      elevation={2}
                      sx={{
                        p: 6,
                        textAlign: 'center',
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        border: '1px solid #dee2e6'
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          mx: 'auto',
                          mb: 3,
                          bgcolor: 'rgba(52, 152, 219, 0.1)'
                        }}
                      >
                        <ImageIcon sx={{ fontSize: 40, color: '#3498db' }} />
                      </Avatar>
                      <Typography variant="h5" sx={{ color: '#495057', mb: 2, fontWeight: 600 }}>
                        {t('storeImages.noImages')}
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#6c757d', mb: 3 }}>
                        {t('storeImages.noImages')}
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<UploadIcon />}
                        onClick={() => setUploadDialogOpen(true)}
                        sx={{
                          background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1a252f 0%, #2980b9 100%)'
                          },
                          px: 4,
                          py: 1.5,
                          borderRadius: 2,
                          fontWeight: 600,
                          textTransform: 'none'
                        }}
                      >
                        {t('storeImages.uploadImages')}
                      </Button>
                    </Paper>
                  ) : (
                    <>
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: 'repeat(2, 1fr)',
                          sm: 'repeat(2, 1fr)',
                          md: 'repeat(3, 1fr)',
                          lg: 'repeat(4, 1fr)',
                          xl: 'repeat(5, 1fr)'
                        },
                        gap: { xs: 1.5, sm: 2, md: 2.5 },
                        width: '100%'
                      }}>
                        {images.map((image, index) => (
                          <Box
                            key={image.id}
                            sx={{
                              width: '100%',
                              minWidth: 0
                            }}
                          >
                            <Zoom in timeout={300 + (index * 100)}>
                              <Card
                                sx={{
                                  borderRadius: 4,
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                  border: '2px solid transparent',
                                  background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #2c3e50 0%, #3498db 100%) border-box',
                                  position: 'relative',
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  '&:hover': {
                                    transform: 'translateY(-12px) scale(1.03)',
                                    boxShadow: '0 25px 50px rgba(52, 152, 219, 0.25)',
                                    '&::before': {
                                      opacity: 1
                                    }
                                  },
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(44, 62, 80, 0.1) 0%, rgba(52, 152, 219, 0.1) 100%)',
                                    opacity: 0,
                                    transition: 'opacity 0.4s ease',
                                    zIndex: 1,
                                    pointerEvents: 'none'
                                  }
                                }}
                                onClick={(e) => {
                                  // If clicking on checkbox area (handled by bubbling check, but here we can toggle if not specific targets)
                                  if (selectedIds.size > 0) {
                                    toggleSelect(image.id);
                                  } else {
                                    handleImageClick(image);
                                  }
                                }}
                              >
                                {/* Selection Checkbox */}
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 12,
                                    left: 12,
                                    zIndex: 5,
                                    opacity: selectedIds.has(image.id) ? 1 : 0,
                                    transform: selectedIds.has(image.id) ? 'scale(1)' : 'scale(0.8)',
                                    transition: 'all 0.2s ease',
                                    '.MuiCard-root:hover &': {
                                      opacity: 1,
                                      transform: 'scale(1)'
                                    }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleSelect(image.id);
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    sx={{
                                      bgcolor: selectedIds.has(image.id) ? '#3498db' : 'rgba(255,255,255,0.9)',
                                      color: selectedIds.has(image.id) ? 'white' : '#95a5a6',
                                      '&:hover': {
                                        bgcolor: selectedIds.has(image.id) ? '#2980b9' : 'white',
                                        color: selectedIds.has(image.id) ? 'white' : '#3498db',
                                      },
                                      border: selectedIds.has(image.id) ? 'none' : '1px solid #bdc3c7',
                                      width: 32,
                                      height: 32
                                    }}
                                  >
                                    {selectedIds.has(image.id) ? <CheckBoxIcon fontSize="small" /> : <CheckBoxOutlineBlankIcon fontSize="small" />}
                                  </IconButton>
                                </Box>
                                <Box sx={{ position: 'relative', zIndex: 2 }}>
                                  {isHeicImage(image.image_url) ? (
                                    <Box
                                      sx={{
                                        width: '100%',
                                        height: { xs: 250, sm: 250, md: 200, lg: 180, xl: 160 },
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        border: '2px solid #ff9800',
                                        borderRadius: 1
                                      }}
                                    >
                                      <Avatar sx={{
                                        bgcolor: 'rgba(255, 152, 0, 0.2)',
                                        mb: 1,
                                        width: 40,
                                        height: 40
                                      }}>
                                        <ErrorIcon sx={{ fontSize: 20, color: '#ff9800' }} />
                                      </Avatar>
                                      <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600, textAlign: 'center', px: 1 }}>
                                        HEIC Format
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <>
                                      <CardMedia
                                        component="img"
                                        image={image.image_url}
                                        alt={`XHS Image #${image.id}`}
                                        sx={{
                                          width: '100%',
                                          height: { xs: 250, sm: 250, md: 200, lg: 180, xl: 160 },
                                          objectFit: 'contain',
                                          backgroundColor: '#f5f5f5',
                                          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                          filter: 'brightness(1) saturate(1.1)',
                                          '&:hover': {
                                            transform: 'scale(1.05)',
                                            filter: 'brightness(1.1) saturate(1.2)'
                                          }
                                        }}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                      <Box
                                        sx={{
                                          width: '100%',
                                          height: { xs: 250, sm: 250, md: 200, lg: 180, xl: 160 },
                                          display: 'none',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                          flexDirection: 'column',
                                          position: 'relative'
                                        }}
                                      >
                                        <Avatar sx={{
                                          bgcolor: 'rgba(52, 152, 219, 0.1)',
                                          mb: 2,
                                          width: 48,
                                          height: 48,
                                          animation: 'float 3s ease-in-out infinite'
                                        }}>
                                          <ImageIcon sx={{ fontSize: 28, color: '#3498db' }} />
                                        </Avatar>
                                        <Typography variant="body2" sx={{ color: '#6c757d', fontWeight: 500 }}>
                                          Image not available
                                        </Typography>
                                      </Box>
                                    </>
                                  )}
                                </Box>

                                {/* Overlay Actions */}
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, transparent 30%, transparent 70%, rgba(0,0,0,0.3) 100%)',
                                    opacity: 0,
                                    transition: 'opacity 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'flex-end',
                                    p: 2
                                  }}
                                  className="card-overlay"
                                >
                                  <Stack direction="row" spacing={1}>
                                    <Tooltip title={t('storeImages.viewDetails')}>
                                      <IconButton
                                        size="small"
                                        sx={{
                                          bgcolor: 'rgba(255,255,255,0.9)',
                                          color: '#3498db',
                                          '&:hover': {
                                            bgcolor: 'white',
                                            transform: 'scale(1.1)'
                                          }
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleImageClick(image);
                                        }}
                                      >
                                        <ViewIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('storeImages.moreOptions')}>
                                      <IconButton
                                        size="small"
                                        sx={{
                                          bgcolor: 'rgba(255,255,255,0.9)',
                                          color: '#3498db',
                                          '&:hover': {
                                            bgcolor: 'white',
                                            transform: 'scale(1.1)'
                                          }
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMenuOpen(e, image);
                                        }}
                                      >
                                        <MoreVertIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </Box>

                                <CardActions sx={{ p: { xs: 1, sm: 1.5, md: 2 }, pt: { xs: 1.5, sm: 2 }, justifyContent: 'center', position: 'relative', zIndex: 2 }}>
                                  <Button
                                    size="small"
                                    startIcon={<ViewIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleImageClick(image);
                                    }}
                                    sx={{
                                      background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                                      color: 'white',
                                      textTransform: 'none',
                                      fontWeight: 600,
                                      px: { xs: 1.5, sm: 2.5, md: 3 },
                                      py: { xs: 0.5, sm: 0.75, md: 1 },
                                      borderRadius: 2,
                                      fontSize: { xs: '0.75rem', sm: '0.875rem', md: '0.9375rem' },
                                      minHeight: { xs: 32, sm: 36, md: 40 },
                                      boxShadow: '0 4px 15px rgba(52, 152, 219, 0.3)',
                                      '&:hover': {
                                        background: 'linear-gradient(135deg, #1a252f 0%, #2980b9 100%)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 20px rgba(52, 152, 219, 0.4)'
                                      },
                                      transition: 'all 0.3s ease',
                                      '& .MuiButton-startIcon': {
                                        marginRight: { xs: 0.5, sm: 1 }
                                      }
                                    }}
                                  >
                                    {t('storeImages.viewDetails')}
                                  </Button>
                                </CardActions>
                              </Card>
                            </Zoom>
                          </Box>
                        ))}
                      </Box>

                      {/* Enhanced Pagination */}
                      {totalPages > 1 && (
                        <Paper
                          elevation={2}
                          sx={{
                            p: 3,
                            mt: 4,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                            border: '1px solid #dee2e6'
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={2}>
                            <Typography variant="body2" sx={{ color: '#6c757d', fontWeight: 500 }}>
                              Page {currentPage} of {totalPages} • Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} images
                            </Typography>
                            <Pagination
                              count={totalPages}
                              page={currentPage}
                              onChange={handlePageChange}
                              color="primary"
                              size="large"
                              sx={{
                                '& .MuiPaginationItem-root': {
                                  borderRadius: 2,
                                  fontWeight: 600,
                                  '&.Mui-selected': {
                                    background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                                    color: 'white',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #1a252f 0%, #2980b9 100%)'
                                    }
                                  }
                                }
                              }}
                            />
                            <Box sx={{ minWidth: 120 }}>
                              <FormControl fullWidth size="small">
                                <InputLabel id="items-per-page-label">Per Page</InputLabel>
                                <Select
                                  labelId="items-per-page-label"
                                  id="items-per-page"
                                  value={itemsPerPage}
                                  label="Per Page"
                                  onChange={handlePageSizeChange}
                                  sx={{ borderRadius: 2 }}
                                >
                                  {[10, 20, 30, 40, 50].map((size) => (
                                    <MenuItem key={size} value={size}>
                                      {size}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          </Stack>
                        </Paper>
                      )}
                    </>
                  )}
                </Box>
              </Fade>
            )}

            {/* Enhanced Professional Image Detail Dialog */}
            <Dialog
              open={dialogOpen}
              onClose={handleCloseDialog}
              maxWidth="lg"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }
              }}
            >
              {selectedImage && (
                <>
                  <DialogTitle
                    sx={{
                      background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 4,
                      px: 4,
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Background Pattern */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.1,
                        backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                      }}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, position: 'relative', zIndex: 1 }}>
                      <Avatar sx={{
                        bgcolor: 'rgba(255,255,255,0.25)',
                        width: 56,
                        height: 56,
                        border: '2px solid rgba(255,255,255,0.3)',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                      }}>
                        <ImageIcon sx={{ fontSize: 28, color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, fontSize: '1.5rem' }}>
                          {t('storeImages.xhsImageDetails')}
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.95, fontSize: '1rem' }}>
                          {t('storeImages.comprehensiveImageInfo')}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      onClick={handleCloseDialog}
                      sx={{
                        color: 'white',
                        bgcolor: 'rgba(255,255,255,0.15)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.25)',
                          transform: 'scale(1.05)'
                        },
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent sx={{ p: 0, bgcolor: '#f8f9fa' }}>
                    <Grid container>
                      <Grid item xs={12} md={7}>
                        <Box sx={{
                          p: 4,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: 500
                        }}>
                          {isHeicImage(selectedImage.image_url) ? (
                            <Box
                              sx={{
                                width: '100%',
                                height: 400,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                borderRadius: 3,
                                flexDirection: 'column',
                                border: '2px dashed #dee2e6',
                                p: 4
                              }}
                            >
                              <Avatar sx={{
                                bgcolor: 'rgba(255, 152, 0, 0.1)',
                                mb: 3,
                                width: 80,
                                height: 80
                              }}>
                                <ErrorIcon sx={{ fontSize: 40, color: '#ff9800' }} />
                              </Avatar>
                              <Typography variant="h6" sx={{ color: '#495057', mb: 1, fontWeight: 600, textAlign: 'center' }}>
                                HEIC Format Not Supported
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#6c757d', mb: 3, textAlign: 'center', maxWidth: 400 }}>
                                HEIC images cannot be displayed directly in web browsers. Please download the image to view it, or convert it to a supported format (JPEG, PNG, etc.).
                              </Typography>
                              <Button
                                variant="contained"
                                component="a"
                                href={selectedImage.image_url}
                                download
                                sx={{
                                  background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                                  '&:hover': {
                                    background: 'linear-gradient(135deg, #1a252f 0%, #2980b9 100%)',
                                  },
                                  px: 3,
                                  py: 1.5,
                                  borderRadius: 2,
                                  fontWeight: 600,
                                  textTransform: 'none'
                                }}
                              >
                                Download Image
                              </Button>
                            </Box>
                          ) : (
                            <>
                              <Box
                                component="img"
                                src={selectedImage.image_url}
                                alt={`XHS Image #${selectedImage.id}`}
                                sx={{
                                  maxWidth: '100%',
                                  maxHeight: 450,
                                  objectFit: 'contain',
                                  borderRadius: 3,
                                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'scale(1.02)',
                                    boxShadow: '0 12px 40px rgba(0,0,0,0.18)'
                                  }
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <Box
                                sx={{
                                  width: '100%',
                                  height: 400,
                                  display: 'none',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                  borderRadius: 3,
                                  flexDirection: 'column',
                                  border: '2px dashed #dee2e6'
                                }}
                              >
                                <Avatar sx={{
                                  bgcolor: 'rgba(52, 152, 219, 0.1)',
                                  mb: 3,
                                  width: 80,
                                  height: 80
                                }}>
                                  <ImageIcon sx={{ fontSize: 40, color: '#3498db' }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ color: '#495057', mb: 1, fontWeight: 600 }}>
                                  Image not available
                                </Typography>
                              </Box>
                            </>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <Box sx={{
                          p: 4,
                          minHeight: 500,
                          display: 'flex',
                          flexDirection: 'column'
                        }}>
                          <Typography variant="h5" sx={{
                            mb: 3,
                            color: '#2c3e50',
                            fontWeight: 700,
                            borderBottom: '2px solid #3498db',
                            pb: 1
                          }}>
                            {t('storeImages.imageDetails')}
                          </Typography>

                          <Stack spacing={3}>
                            {/* Created Date */}
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600 }}>
                                {t('storeImages.createdDate')}
                              </Typography>
                              <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 0.5 }}>
                                {new Date(selectedImage.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#6c757d' }}>
                                {new Date(selectedImage.created_at).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                            </Box>

                            {selectedImage.updated_at !== selectedImage.created_at && (
                              <Box>
                                <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600 }}>
                                  {t('storeImages.lastUpdated')}
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 0.5 }}>
                                  {new Date(selectedImage.updated_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#6c757d' }}>
                                  {new Date(selectedImage.updated_at).toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </Typography>
                              </Box>
                            )}

                            {/* Image URL */}
                            <Box>
                              <Typography variant="subtitle2" sx={{ color: '#6c757d', mb: 1, fontWeight: 600 }}>
                                {t('storeImages.imageUrl')}
                              </Typography>
                              <Box
                                sx={{
                                  p: 2,
                                  bgcolor: '#f8f9fa',
                                  borderRadius: 2,
                                  border: '1px solid #dee2e6',
                                  wordBreak: 'break-all',
                                  fontFamily: 'monospace',
                                  fontSize: '0.85rem',
                                  color: '#495057',
                                  lineHeight: 1.4
                                }}
                              >
                                {selectedImage.image_url}
                              </Box>
                            </Box>
                          </Stack>
                        </Box>
                      </Grid>
                    </Grid>
                  </DialogContent>
                </>
              )}
            </Dialog>

            {/* Context Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem
                onClick={() => {
                  if (selectedImage) {
                    handleDeleteImage(selectedImage.id);
                  }
                  handleMenuClose();
                }}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon sx={{ mr: 1 }} />
                {t('storeImages.deleteImage')}
              </MenuItem>
            </Menu>

            {/* Enhanced Professional Upload Dialog */}
            <Dialog
              open={uploadDialogOpen}
              onClose={handleCloseUploadDialog}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }
              }}
            >
              <DialogTitle
                sx={{
                  background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 4,
                  px: 4,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Background Pattern */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.1,
                    backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, position: 'relative', zIndex: 1 }}>
                  <Avatar sx={{
                    bgcolor: 'rgba(255,255,255,0.25)',
                    width: 56,
                    height: 56,
                    border: '2px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  }}>
                    <UploadIcon sx={{ fontSize: 28, color: 'white' }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5, fontSize: '1.5rem' }}>
                      {t('storeImages.uploadDialogTitle')}
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.95, fontSize: '1rem' }}>
                      {t('storeImages.uploadDialogSubtitle')}
                    </Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={handleCloseUploadDialog}
                  sx={{
                    color: 'white',
                    bgcolor: 'rgba(255,255,255,0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.25)',
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent sx={{ p: 4 }}>
                <Stack spacing={3}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      border: '1px solid #dee2e6'
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                      {t('storeImages.uploadRequirements')}
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2" sx={{ color: '#6c757d', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#3498db' }} />
                        {t('storeImages.supportedFormats')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6c757d', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#3498db' }} />
                        {t('storeImages.maxFileSize')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6c757d', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#3498db' }} />
                        {t('storeImages.storageLocation')}
                      </Typography>
                    </Stack>
                  </Paper>

                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="image-upload"
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                  />
                  <Button
                    component="label"
                    htmlFor="image-upload"
                    variant="outlined"
                    fullWidth
                    sx={{
                      p: 4,
                      border: '2px dashed #3498db',
                      borderRadius: 3,
                      background: selectedFiles.length > 0
                        ? 'linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(41, 128, 185, 0.05) 100%)'
                        : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: '#2980b9',
                        backgroundColor: selectedFiles.length > 0
                          ? 'linear-gradient(135deg, rgba(52, 152, 219, 0.1) 0%, rgba(41, 128, 185, 0.1) 100%)'
                          : 'linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(41, 128, 185, 0.05) 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(52, 152, 219, 0.15)'
                      }
                    }}
                  >
                    <Stack alignItems="center" spacing={2}>
                      <Avatar
                        sx={{
                          width: 64,
                          height: 64,
                          bgcolor: selectedFiles.length > 0
                            ? 'rgba(52, 152, 219, 0.1)'
                            : 'rgba(52, 152, 219, 0.05)'
                        }}
                      >
                        <UploadIcon
                          sx={{
                            fontSize: 32,
                            color: selectedFiles.length > 0 ? '#3498db' : '#6c757d'
                          }}
                        />
                      </Avatar>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant="h6"
                          sx={{
                            color: selectedFiles.length > 0 ? '#3498db' : '#495057',
                            fontWeight: 600,
                            mb: 1
                          }}
                        >
                          {selectedFiles.length > 0
                            ? `${selectedFiles.length} ${selectedFiles.length > 1 ? t('storeImages.filesSelectedPlural') : t('storeImages.filesSelected')}`
                            : t('storeImages.clickToSelect')
                          }
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6c757d' }}>
                          {selectedFiles.length > 0
                            ? t('storeImages.readyToUpload')
                            : t('storeImages.dragDropHint')
                          }
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#adb5bd', mt: 1, display: 'block' }}>
                          {t('storeImages.maxFileSizeHint')}
                        </Typography>
                      </Box>
                    </Stack>
                  </Button>

                  {selectedFiles.length > 0 && (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid #dee2e6'
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                        {t('storeImages.selectedFiles')} ({selectedFiles.length})
                      </Typography>
                      <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                        <Stack spacing={2}>
                          {selectedFiles.map((file, index) => (
                            <Paper
                              key={index}
                              elevation={0}
                              sx={{
                                p: 2,
                                bgcolor: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                borderRadius: 2,
                                border: '1px solid #dee2e6'
                              }}
                            >
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ bgcolor: 'rgba(52, 152, 219, 0.1)', width: 40, height: 40 }}>
                                  <ImageIcon sx={{ color: '#3498db', fontSize: 20 }} />
                                </Avatar>
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 0.5 }}>
                                    {file.name}
                                  </Typography>
                                  <Stack direction="row" spacing={2} alignItems="center">
                                    <Chip
                                      label={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: '0.75rem' }}
                                    />
                                    <Chip
                                      label={file.type.split('/')[1]?.toUpperCase() || 'IMAGE'}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                      sx={{ fontSize: '0.75rem' }}
                                    />
                                  </Stack>
                                </Box>
                                <CheckCircleIcon sx={{ color: '#3498db', fontSize: 24 }} />
                              </Stack>
                            </Paper>
                          ))}
                        </Stack>
                      </Box>
                    </Paper>
                  )}

                  {uploadingFiles.length > 0 && (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid #dee2e6'
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                        {t('storeImages.uploadProgress')}
                      </Typography>
                      <Stack spacing={2}>
                        {uploadingFiles.map((file, index) => (
                          <Paper
                            key={index}
                            elevation={0}
                            sx={{
                              p: 2,
                              bgcolor: file.status === 'completed'
                                ? 'linear-gradient(135deg, rgba(40, 167, 69, 0.05) 0%, rgba(32, 201, 151, 0.05) 100%)'
                                : file.status === 'error'
                                  ? 'linear-gradient(135deg, rgba(220, 53, 69, 0.05) 0%, rgba(255, 107, 107, 0.05) 100%)'
                                  : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                              borderRadius: 2,
                              border: `1px solid ${file.status === 'completed' ? '#28a745' : file.status === 'error' ? '#dc3545' : '#dee2e6'}`
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar sx={{
                                bgcolor: file.status === 'completed'
                                  ? 'rgba(52, 152, 219, 0.1)'
                                  : file.status === 'error'
                                    ? 'rgba(220, 53, 69, 0.1)'
                                    : 'rgba(52, 152, 219, 0.1)',
                                width: 40,
                                height: 40
                              }}>
                                {file.status === 'completed' ? (
                                  <CheckCircleIcon sx={{ color: '#3498db', fontSize: 20 }} />
                                ) : file.status === 'error' ? (
                                  <ErrorIcon sx={{ color: '#dc3545', fontSize: 20 }} />
                                ) : (
                                  <CircularProgress size={20} sx={{ color: '#3498db' }} />
                                )}
                              </Avatar>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 0.5 }}>
                                  {file.name}
                                </Typography>
                                {file.status === 'uploading' && (
                                  <LinearProgress
                                    sx={{
                                      height: 6,
                                      borderRadius: 3,
                                      bgcolor: 'rgba(102, 126, 234, 0.1)',
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: '#3498db'
                                      }
                                    }}
                                  />
                                )}
                                {file.error && (
                                  <Typography variant="caption" sx={{ color: '#dc3545', mt: 1, display: 'block' }}>
                                    {file.error}
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="caption" sx={{
                                color: file.status === 'completed' ? '#3498db' :
                                  file.status === 'error' ? '#dc3545' : '#6c757d',
                                fontWeight: 600
                              }}>
                                {file.status === 'completed' ? t('storeImages.completed') :
                                  file.status === 'error' ? t('storeImages.failed') : t('storeImages.uploadingProgress')}
                              </Typography>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    </Paper>
                  )}

                  {uploading && (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid #3498db',
                        bgcolor: 'linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(41, 128, 185, 0.05) 100%)'
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                        <CircularProgress size={24} sx={{ color: '#3498db' }} />
                        <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 600 }}>
                          {t('storeImages.uploadingToR2')}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'rgba(52, 152, 219, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#3498db'
                          }
                        }}
                      />
                      <Typography variant="body2" sx={{ color: '#6c757d', mt: 1, textAlign: 'center' }}>
                        {uploadProgress.toFixed(0)}{t('storeImages.percentComplete')}
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions sx={{ p: 4, bgcolor: '#f8f9fa' }}>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    onClick={handleUpload}
                    variant="contained"
                    disabled={selectedFiles.length === 0 || uploading}
                    startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1a252f 0%, #2980b9 100%)',
                        transform: 'translateY(-1px)'
                      },
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      color: 'white !important',
                      textTransform: 'none',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {uploading
                      ? `${t('storeImages.uploadingProgress')} (${uploadProgress.toFixed(0)}%)`
                      : `${t('storeImages.uploadAndAdd')} ${selectedFiles.length} ${selectedFiles.length > 1 ? t('storeImages.uploadAndAddFilesPlural') : t('storeImages.uploadAndAddFiles')}`
                    }
                  </Button>
                </Stack>
              </DialogActions>
            </Dialog>

            {/* Duplicate Image Confirmation Dialog */}
            <Dialog
              open={duplicateDialogOpen}
              onClose={() => setDuplicateDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle sx={{
                bgcolor: '#fff3cd',
                color: '#856404',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <ErrorIcon color="inherit" />
                {t('storeImages.duplicateImageDetected')}
              </DialogTitle>
              <DialogContent sx={{ mt: 2 }}>
                <Typography gutterBottom>
                  {t('storeImages.duplicateImageMessage')}
                </Typography>
                <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', mt: 1, bgcolor: '#f8f9fa' }}>
                  <ul style={{ margin: 0, padding: '8px 8px 8px 24px' }}>
                    {duplicateFiles.map((fileName, index) => (
                      <li key={index}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {fileName}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </Paper>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDuplicateDialogOpen(false)} color="inherit">
                  {t('common.cancel') || 'Cancel'}
                </Button>
                <Button
                  onClick={handleSkipDuplicates}
                  variant="contained"
                  color="info"
                  sx={{
                    background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)',
                    color: 'white'
                  }}
                >
                  {t('storeImages.skipDuplicates')}
                </Button>
                <Button
                  onClick={handleConfirmUpload}
                  variant="contained"
                  color="primary"
                  sx={{
                    background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)'
                  }}
                >
                  {t('storeImages.uploadAnyway')}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Create Image Dialog */}
            <Dialog
              open={createImageDialogOpen}
              onClose={handleCloseCreateImageDialog}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
                  border: '1px solid rgba(255,255,255,0.1)'
                }
              }}
            >
              <DialogTitle sx={{
                background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                color: 'white',
                p: 4,
                textAlign: 'center'
              }}>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                  <AutoAwesomeIcon sx={{ fontSize: 32 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {t('storeImages.createImageDialogTitle')}
                  </Typography>
                </Stack>
                <Typography variant="body1" sx={{ mt: 2, opacity: 0.9 }}>
                  {t('storeImages.createImageDialogSubtitle')}
                </Typography>
              </DialogTitle>

              <DialogContent sx={{ p: 4 }}>
                <Stack spacing={4}>
                  {/* Image Upload Section */}
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                      {t('storeImages.uploadBaseImage')}
                    </Typography>
                    <input
                      type="file"
                      id="create-image-upload"
                      accept="image/*"
                      onChange={handleCreateImageFileSelect}
                      style={{ display: 'none' }}
                    />
                    <Button
                      component="label"
                      htmlFor="create-image-upload"
                      variant="outlined"
                      fullWidth
                      sx={{
                        p: 4,
                        border: '2px dashed #3498db',
                        borderRadius: 3,
                        background: createImageFile
                          ? 'linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(41, 128, 185, 0.05) 100%)'
                          : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: '#2980b9',
                          backgroundColor: createImageFile
                            ? 'linear-gradient(135deg, rgba(52, 152, 219, 0.1) 0%, rgba(41, 128, 185, 0.1) 100%)'
                            : 'linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(41, 128, 185, 0.05) 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(52, 152, 219, 0.15)'
                        }
                      }}
                    >
                      <Stack alignItems="center" spacing={2}>
                        <Avatar
                          sx={{
                            width: 64,
                            height: 64,
                            bgcolor: createImageFile
                              ? 'rgba(52, 152, 219, 0.1)'
                              : 'rgba(52, 152, 219, 0.05)'
                          }}
                        >
                          <ImageIcon
                            sx={{
                              fontSize: 32,
                              color: createImageFile ? '#3498db' : '#6c757d'
                            }}
                          />
                        </Avatar>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography
                            variant="h6"
                            sx={{
                              color: createImageFile ? '#3498db' : '#495057',
                              fontWeight: 600,
                              mb: 1
                            }}
                          >
                            {createImageFile
                              ? createImageFile.name
                              : t('storeImages.clickToSelect')
                            }
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6c757d' }}>
                            {createImageFile
                              ? t('storeImages.readyForAIProcessing')
                              : t('storeImages.selectImageBase')
                            }
                          </Typography>
                        </Box>
                      </Stack>
                    </Button>
                  </Box>

                  {/* Prompt Input Section */}
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                      {t('storeImages.aiPromptKeywords')}
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      placeholder={t('storeImages.aiPromptPlaceholder')}
                      value={imagePrompt}
                      onChange={(e) => setImagePrompt(e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3498db'
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3498db',
                            borderWidth: 2
                          }
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#6c757d', mt: 1, display: 'block' }}>
                      {t('storeImages.describePromptHint')}
                    </Typography>
                  </Box>

                  {/* Preview Section */}
                  {createImageFile && (
                    <Box>
                      <Typography variant="h6" sx={{ mb: 2, color: '#2c3e50', fontWeight: 600 }}>
                        {t('storeImages.preview')}
                      </Typography>
                      <Paper
                        elevation={1}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid #dee2e6'
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: 'rgba(52, 152, 219, 0.1)', width: 40, height: 40 }}>
                            <ImageIcon sx={{ color: '#3498db', fontSize: 20 }} />
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 600, mb: 0.5 }}>
                              {createImageFile.name}
                            </Typography>
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Chip
                                label={`${(createImageFile.size / 1024 / 1024).toFixed(2)} MB`}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem' }}
                              />
                              <Chip
                                label={createImageFile.type.split('/')[1]?.toUpperCase() || 'IMAGE'}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ fontSize: '0.75rem' }}
                              />
                            </Stack>
                          </Box>
                          <CheckCircleIcon sx={{ color: '#3498db', fontSize: 24 }} />
                        </Stack>
                      </Paper>
                    </Box>
                  )}

                  {/* Generation Progress */}
                  {generatingImage && (
                    <Paper
                      elevation={1}
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid #3498db',
                        bgcolor: 'linear-gradient(135deg, rgba(52, 152, 219, 0.05) 0%, rgba(41, 128, 185, 0.05) 100%)'
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                        <CircularProgress size={24} sx={{ color: '#3498db' }} />
                        <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 600 }}>
                          {t('storeImages.generatingAIImage')}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" sx={{ color: '#6c757d' }}>
                        {t('storeImages.processingImageHint')}
                      </Typography>
                    </Paper>
                  )}
                </Stack>
              </DialogContent>

              <DialogActions sx={{ p: 4, bgcolor: '#f8f9fa' }}>
                <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    onClick={handleCloseCreateImageDialog}
                    variant="outlined"
                    disabled={generatingImage}
                    sx={{
                      borderColor: '#dee2e6',
                      color: '#6c757d',
                      '&:hover': {
                        borderColor: '#adb5bd',
                        backgroundColor: 'rgba(108, 117, 125, 0.04)'
                      },
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleCreateImage}
                    variant="contained"
                    disabled={!createImageFile || !imagePrompt.trim() || generatingImage}
                    startIcon={generatingImage ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1a252f 0%, #2980b9 100%)',
                        transform: 'translateY(-1px)'
                      },
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      color: 'white !important',
                      textTransform: 'none',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {generatingImage ? t('storeImages.generating') : t('storeImages.createAIImage')}
                  </Button>
                </Stack>
              </DialogActions>
            </Dialog>
          </Box>
        </Paper>
      </Container >
    </>
  );
};

export default StoreImages;
